"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { FileText, Loader2, Search, Upload } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import uploadFile, {
    listFiles,
    readSharingDefaults,
    type FileRecord,
    type UploadOptions,
} from "../api/file";
import { resolveLabwickUploadUrl } from "../utils/pictureUtils";
import { FileSharingPrompt } from "../files/FileSharingPrompt";
import { useAuth } from "../auth/AuthContext";

const ALLOWED_ACCEPT = ".pdf,.txt,.docx";
const ALLOWED_EXTENSIONS = [".pdf", ".txt", ".docx"];

type FilePickerDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (data: { filename: string }) => void;
    uploadOptions?: UploadOptions;
    title?: string;
};

function isAllowedFile(name: string): boolean {
    const base = String(name || "").split("/").pop() || "";
    const ext = base.includes(".") ? `.${base.split(".").pop()!.toLowerCase()}` : "";
    return ALLOWED_EXTENSIONS.includes(ext);
}

function formatSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FilePickerDialog({
    open,
    onOpenChange,
    onSelect,
    uploadOptions,
    title = "Choose file",
}: FilePickerDialogProps) {
    const [files, setFiles] = useState<FileRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { user } = useAuth();
    const userInGroup = Array.isArray((user as any)?.groups) && (user as any).groups.length > 0;
    const [pendingShare, setPendingShare] = useState<{ fileId: string; filename: string } | null>(null);

    useEffect(() => {
        if (!open) return;
        setError("");
        setQuery("");
        setLoading(true);
        listFiles()
            .then((data) => {
                if (data?.success && Array.isArray(data.files)) {
                    setFiles(data.files.filter((f) => isAllowedFile(f.name) || isAllowedFile(f.original)));
                } else {
                    setFiles([]);
                    if (data && !data.success) setError("Failed to load files");
                }
            })
            .catch(() => setError("Failed to load files"))
            .finally(() => setLoading(false));
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return files;
        return files.filter(
            (f) =>
                f.name.toLowerCase().includes(q) ||
                (f.original || "").toLowerCase().includes(q)
        );
    }, [files, query]);

    const handlePick = (name: string) => {
        onSelect({ filename: name });
        onOpenChange(false);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const ext = (file.name.includes(".") ? `.${file.name.split(".").pop()}` : "").toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            setError("Invalid file type. Only PDF, TXT and DOCX are allowed.");
            return;
        }
        setError("");
        setUploading(true);
        uploadFile(file, { ...readSharingDefaults(), ...uploadOptions })
            .then((data) => {
                if (data?.success && data.filename) {
                    if (userInGroup && data.fileId && !uploadOptions?.temporary) {
                        setPendingShare({ fileId: data.fileId, filename: data.filename });
                    } else {
                        onSelect({ filename: data.filename });
                        onOpenChange(false);
                    }
                } else {
                    setError(data?.message || "Failed to upload file");
                }
            })
            .catch((err) => setError(err?.message || "Failed to upload file"))
            .finally(() => setUploading(false));
    };

    const finishPendingShare = () => {
        const share = pendingShare;
        setPendingShare(null);
        if (share) {
            onSelect({ filename: share.filename });
            onOpenChange(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        Upload a new file or pick one from your library (files can be linked to multiple items).
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search files..."
                            className="pl-8"
                        />
                    </div>
                    <Button onClick={handleUploadClick} disabled={uploading}>
                        {uploading ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Upload className="mr-2 h-4 w-4" />
                        )}
                        Upload new
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ALLOWED_ACCEPT}
                        className="hidden"
                        onChange={handleFileChosen}
                    />
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <div className="max-h-[50vh] min-h-[12rem] overflow-y-auto">
                    {loading ? (
                        <div className="flex h-48 items-center justify-center text-muted-foreground">
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Loading files...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex h-48 flex-col items-center justify-center gap-1 text-muted-foreground">
                            <FileText className="h-8 w-8" />
                            <span className="text-sm">
                                {files.length === 0 ? "No files uploaded yet" : "No files match your search"}
                            </span>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {filtered.map((file) => {
                                const displayName = file.original || file.name.split("/").pop() || file.name;
                                const sizeLabel = formatSize(file.size);
                                const url = resolveLabwickUploadUrl(file.name);
                                return (
                                    <button
                                        key={file.uid}
                                        type="button"
                                        onClick={() => handlePick(file.name)}
                                        className={cn(
                                            "group flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                                            "hover:border-primary hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
                                        )}
                                        title={file.name}
                                    >
                                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                                            <FileText className="h-5 w-5" />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm font-medium group-hover:text-foreground">
                                                {displayName}
                                            </span>
                                            <span className="block truncate text-xs text-muted-foreground">
                                                {sizeLabel}
                                                {file.relations?.length
                                                    ? ` · linked to ${file.relations.length} item${file.relations.length === 1 ? "" : "s"}`
                                                    : ""}
                                            </span>
                                            {url ? (
                                                <span
                                                    className="mt-0.5 block truncate text-xs text-blue-600 dark:text-blue-400"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="hover:underline"
                                                    >
                                                        Preview
                                                    </a>
                                                </span>
                                            ) : null}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </DialogContent>
            <FileSharingPrompt
                open={pendingShare !== null}
                fileId={pendingShare?.fileId ?? null}
                filename={pendingShare?.filename}
                onResolved={finishPendingShare}
            />
        </Dialog>
    );
}

export default FilePickerDialog;
